import { useState, type ReactNode } from "react";
import { isUnavailableStoredUrl, isStoredFileUrl } from "./fileRoute";
import { downloadStoredFile } from "./storedFileFetcher";

function isSafeLinkUrl(url: string): boolean {
  if (url.startsWith("/")) return !url.startsWith("//") && !url.startsWith("/\\");
  try {
    return ["http:", "https:", "mailto:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

/**
 * A download control for a stored file (story 25240): fetches through the
 * signed-in /api/files route and saves it. A storage URL with no source renders
 * as plain "unavailable" text — never a link to storage. Any other URL stays an
 * ordinary link only for http(s), mailto or a relative path; any other scheme
 * (javascript:, data:, …) is shown as unavailable.
 */
export function StoredFileLink({
  url,
  filename,
  className,
  children,
  "data-testid": testId,
}: {
  url: string;
  filename?: string | null;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!isStoredFileUrl(url)) {
    if (isUnavailableStoredUrl(url) || !isSafeLinkUrl(url)) {
      return (
        <span className="text-primary-gray-light-500" title="This file is no longer available" data-testid={testId}>
          {children}
        </span>
      );
    }
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <>
      <button
        type="button"
        className={`text-left ${className ?? ""}`}
        data-testid={testId}
        onClick={(e) => {
          e.stopPropagation();
          setFailed(false);
          downloadStoredFile(url, filename).catch(() => setFailed(true));
        }}
      >
        {children}
      </button>
      {failed && (
        <span role="alert" className="ml-1 text-xs text-primary-error-700">
          Download failed
        </span>
      )}
    </>
  );
}

export default StoredFileLink;
